import React, { useEffect, useState, memo, createElement } from 'react';
import { CheckIcon, ShieldCheckIcon, MailIcon, PrinterIcon, InfinityIcon, StarIcon, ArrowDownIcon, ZapIcon, BrainIcon, ClipboardListIcon, TargetIcon, MedalIcon } from 'lucide-react';
import { CountdownTimer } from '../components/CountdownTimer';
import { FAQAccordion } from '../components/FAQAccordion';
// Non-render-blocking font loader
function useGoogleFonts() {
  useEffect(() => {
    // Preconnect to Google Fonts origins
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    // Preload hero image for faster LCP
    const preloadImg = document.createElement('link');
    preloadImg.rel = 'preload';
    preloadImg.as = 'image';
    preloadImg.href = 'https://cdn.magicpatterns.com/uploads/ruvk9w8vaTqG7qHh91vjrZ/mockup_principal.webp';
    preloadImg.type = 'image/webp';
    document.head.appendChild(preloadImg);
    // Load fonts asynchronously (non-render-blocking)
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Open+Sans:wght@400;500;600;700&display=swap';
    link.media = 'print';
    link.onload = () => {
      link.media = 'all';
    };
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(preconnect1);
      document.head.removeChild(preconnect2);
      document.head.removeChild(preloadImg);
      document.head.removeChild(link);
    };
  }, []);
}
const CHECKOUT_URL = 'https://totaliadigital.com/cart/50438072008897:1';
const CHECKOUT_URL_PREMIUM = 'https://totaliadigital.com/cart/50438077972673:1';
function CTAButton({
  children,
  className = '',
  scrollToOffer = false,
  href
}: {
  children: React.ReactNode;
  className?: string;
  scrollToOffer?: boolean;
  href?: string;
}) {
  const handleClick = (e: React.MouseEvent) => {
    if (scrollToOffer) {
      e.preventDefault();
      const el = document.getElementById('compra');
      if (el) el.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const finalHref = scrollToOffer ? '#compra' : href || CHECKOUT_URL;
  return <a href={finalHref} target={scrollToOffer ? undefined : '_blank'} rel={scrollToOffer ? undefined : 'noopener noreferrer'} onClick={handleClick} className={`btn-cta inline-block text-white font-bold uppercase tracking-wide rounded-full px-8 py-5 text-lg md:text-xl shadow-xl cursor-pointer text-center transition-transform ${className}`} style={{
    backgroundColor: 'var(--color-orange)',
    fontFamily: 'var(--font-heading)',
    letterSpacing: '0.05em'
  }} data-id="element-30" data-mp="3j4sfhpd5ju68">
      {children}
    </a>;
}
function StarRating({
  count = 5
}: {
  count?: number;
}) {
  return <div className="flex items-center gap-1" data-id="element-31" data-mp="ub2awsa1yx9j">
      {Array.from({
      length: count
    }).map((_, i) => <StarIcon key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-32" data-mp="fgxfk8o29ixe" data-mpi={i} />)}
    </div>;
}
export function LandingPage() {
  useGoogleFonts();
  const [selectedPlan, setSelectedPlan] = useState<'base' | 'premium'>('premium');
  return <div className="w-full min-h-screen bg-white" style={{
    fontFamily: 'var(--font-body)'
  }} data-id="element-33" data-mp="be118u5k08z9">
      {/* ── STICKY TOP URGENCY BAR ── */}
      <div className="sticky top-0 z-40 w-full py-2 px-4 text-center text-white text-sm font-semibold flex items-center justify-center gap-2" style={{
      backgroundColor: '#c0392b',
      fontFamily: 'var(--font-heading)'
    }} data-id="element-34" data-mp="1mwn1wnpcs5h">
        <span className="whitespace-nowrap" data-id="element-35" data-mp="3nw7dr5oty3zw">
          ⚡️ OFERTA SOLO POR HOY — Finalizá en:
        </span>
        <CountdownTimer compact className="text-white text-sm font-bold" data-id="element-36" data-mp="3gt0ygpsyw3vr" />
      </div>

      {/* ── HERO SECTION ── */}
      <section className="w-full bg-white pt-10 pb-8 px-4" data-id="element-37" data-mp="370v4jaj5ncdx">
        <div className="max-w-3xl mx-auto text-center" data-id="element-38" data-mp="36zaadm0r5vgl">
          {/* Audience badge */}
          <div className="inline-block px-4 py-2 rounded-full text-white text-sm font-bold uppercase tracking-widest mb-6" style={{
          backgroundColor: 'var(--color-green-dark)',
          fontFamily: 'var(--font-heading)'
        }} data-id="element-39" data-mp="3220bzgvdy9gm">
            Para Acompañantes Terapéuticos y Cuidadores
          </div>

          {/* Main headline */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-4 uppercase" style={{
          fontFamily: 'var(--font-heading)',
          minHeight: '120px'
        }} data-id="element-40" data-mp="2j4z85zdkife9">
            El material que te ahorra horas de planificación y te permite llegar
            a cada encuentro con{' '}
            <span style={{
            color: 'var(--color-green-dark)'
          }} data-id="element-41" data-mp="ttlr5mg9itqk">
              todo listo
            </span>
          </h1>

          {/* Product image — main mockup */}
          <div className="my-8 flex justify-center" style={{
          minHeight: '300px'
        }} data-id="element-42" data-mp="13josc9xjfdzz">
            <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="inline-block w-full max-w-md" style={{
            aspectRatio: '1 / 1'
          }} data-id="element-43" data-mp="av9f9sxyv5ra">
              <img src="https://cdn.magicpatterns.com/uploads/ruvk9w8vaTqG7qHh91vjrZ/mockup_principal.webp" alt="Kit Mente Clara & Activa — Cuadernillo digital de estimulación cognitiva" className="w-full h-full object-contain drop-shadow-2xl cursor-pointer hover:scale-105 transition-transform duration-300" width={448} height={448} sizes="(max-width: 768px) 90vw, 448px" fetchPriority="high" decoding="async" data-id="element-44" data-mp="krw22n9us6vy" />
            </a>
          </div>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-gray-700 mb-6 leading-relaxed" data-id="element-45" data-mp="nuici5i6six2">
            Más de <strong data-id="element-46" data-mp="ns5whxvzoimn">150 actividades de gimnasia mental</strong> claras,
            guiadas y listas para usar con adultos mayores.{' '}
            <span className="font-semibold" style={{
            color: 'var(--color-green-dark)'
          }} data-id="element-47" data-mp="xkk0yyegc3ci">
              Sin preparación previa. Sin improvisar.
            </span>
          </p>

          {/* Audience tags */}
          <div className="flex flex-wrap justify-center gap-3 mb-8" style={{
          minHeight: '40px'
        }} data-id="element-48" data-mp="564va2iciwv">
            <span className="flex items-center gap-2 bg-green-50 text-green-800 px-4 py-2 rounded-full text-sm font-semibold border border-green-200" data-id="element-49" data-mp="2bqym2sozyi0q">
              <CheckIcon className="w-4 h-4" data-id="element-50" data-mp="21ulpnn22hq2a" /> Acompañantes Terapéuticos
            </span>
            <span className="flex items-center gap-2 bg-green-50 text-green-800 px-4 py-2 rounded-full text-sm font-semibold border border-green-200" data-id="element-51" data-mp="26snb2emyu1gx">
              <CheckIcon className="w-4 h-4" data-id="element-52" data-mp="2ujkbau5efacc" /> Cuidadores domiciliarios
            </span>
            <span className="flex items-center gap-2 bg-green-50 text-green-800 px-4 py-2 rounded-full text-sm font-semibold border border-green-200" data-id="element-53" data-mp="2zhc4rsloz4in">
              <CheckIcon className="w-4 h-4" data-id="element-54" data-mp="2pjehb7bnpbr3" /> Trabajadores de geriátrico
            </span>
          </div>

          {/* Social proof */}
          <div className="flex items-center justify-center gap-3 mb-8" data-id="element-55" data-mp="1a24mj90q6omy">
            <StarRating data-id="element-56" data-mp="1e4rhhisp0pod" />
            <span className="text-gray-700 font-semibold" data-id="element-57" data-mp="1e5aqub4a25qs">
              4.9/5 · Más de 200 acompañantes ya lo usan
            </span>
          </div>

          {/* Primary CTA */}
          <CTAButton scrollToOffer className="w-full max-w-md mx-auto block text-xl py-6" data-id="element-58" data-mp="20ry3d530nyea">
            ¡QUIERO EL KIT AHORA!
          </CTAButton>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-6 mt-5 text-gray-500 text-sm flex-wrap" data-id="element-59" data-mp="1rtmeb7hst8ny">
            <span className="flex items-center gap-1.5" data-id="element-60" data-mp="2oi267a8dru48">
              <ShieldCheckIcon className="w-4 h-4 text-green-600" data-id="element-61" data-mp="2kgglx3tiodk0" />
              Compra 100% segura
            </span>
            <span className="flex items-center gap-1.5" data-id="element-62" data-mp="2fp8c46o9yq6b">
              <MailIcon className="w-4 h-4 text-green-600" data-id="element-63" data-mp="2fnxc8hxya2kh" />
              Acceso inmediato por mail
            </span>
          </div>
        </div>
      </section>

      {/* ── PAIN POINTS ── */}
      <section className="w-full bg-gray-50 py-16 px-4" data-id="element-64" data-mp="388dzp0gj1fnr">
        <div className="max-w-3xl mx-auto" data-id="element-65" data-mp="33f2pcpciagr3">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4 uppercase" style={{
          fontFamily: 'var(--font-heading)'
        }} data-id="element-66" data-mp="3aqz5z9nj5728">
            ¿Te suena alguna de estas situaciones?
          </h2>
          <p className="text-center text-gray-600 mb-10 text-lg" data-id="element-67" data-mp="3knvirrkgofcl">
            Si trabajás con adultos mayores, probablemente sí.
          </p>

          <div className="space-y-4" data-id="element-68" data-mp="3tq4toijn6aqi">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-69" data-mp="3tp1xpw7gpi3q">
              <span className="text-2xl flex-shrink-0" data-id="element-70" data-mp="2hpivnu1xgqz">😮‍💨</span>
              <p className="text-gray-700 leading-relaxed" data-id="element-71" data-mp="7a0kqw8crcm6">
                <strong data-id="element-72" data-mp="c9dfs49dnpzu">
                  Llegás al encuentro pensando qué actividad hacer...
                </strong>{' '}
                y nada te convence del todo.
              </p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-73" data-mp="la1ku9gsmg0l">
              <span className="text-2xl flex-shrink-0" data-id="element-74" data-mp="1g7gk2kzcz7x5">🔍</span>
              <p className="text-gray-700 leading-relaxed" data-id="element-75" data-mp="1g53wnsau05cp">
                <strong data-id="element-76" data-mp="1l7cq0prm99fc">Buscás material online</strong> y lo que encontrás es
                infantil, demasiado clínico o requiere adaptación constante que
                no tenés tiempo de hacer.
              </p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-77" data-mp="1uxflqxq4h1hd">
              <span className="text-2xl flex-shrink-0" data-id="element-78" data-mp="1z02ed0umaqfm">🔄</span>
              <p className="text-gray-700 leading-relaxed" data-id="element-79" data-mp="23vhnxglle068">
                <strong data-id="element-80" data-mp="28w5igc914z57">Terminás repitiendo las mismas actividades</strong>{' '}
                porque crear algo nuevo lleva tiempo que no sobra.
              </p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-81" data-mp="ehuq3tt87muo">
              <span className="text-2xl flex-shrink-0" data-id="element-82" data-mp="jgo4dogc7hvw">😤</span>
              <p className="text-gray-700 leading-relaxed" data-id="element-83" data-mp="jh7nv9lmyec1">
                <strong data-id="element-84" data-mp="2wrhaaqsb1dgt">
                  Cuando la actividad no engancha, el encuentro se desarma.
                </strong>{' '}
                Y eso te pesa.
              </p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-85" data-mp="2ny480y8cozi2">
              <span className="text-2xl flex-shrink-0" data-id="element-86" data-mp="2j0vooubgupf8">🧠</span>
              <p className="text-gray-700 leading-relaxed" data-id="element-87" data-mp="3fptia97hchat">
                <strong data-id="element-88" data-mp="3fpa9bhzt538x">
                  Querés ofrecer estimulación cognitiva real, con base
                  científica,
                </strong>{' '}
                pero no siempre encontrás material que cumpla eso.
              </p>
            </div>
          </div>

          <div className="mt-10 rounded-xl p-6 text-center text-white" style={{
          backgroundColor: 'var(--color-green-dark)'
        }} data-id="element-89" data-mp="31p911hh7qtjf">
            <p className="text-xl font-bold" style={{
            fontFamily: 'var(--font-heading)'
          }} data-id="element-90" data-mp="1qa3t9z2864sy">
              El problema no sos vos.
            </p>
            <p className="mt-2 text-green-100 text-lg" data-id="element-91" data-mp="1wq3wywmmok3n">
              Es que nadie te da una herramienta realmente lista para usar.
            </p>
          </div>
        </div>
      </section>

      {/* ── BRIDGE / SOLUTION ── */}
      <section className="w-full py-16 px-4 text-white" style={{
      backgroundColor: 'var(--color-green-dark)'
    }} data-id="element-92" data-mp="1nsjwvr0i4tum">
        <div className="max-w-3xl mx-auto text-center" data-id="element-93" data-mp="1dtkvmti2g0hn">
          <h2 className="text-3xl md:text-4xl font-bold uppercase mb-2" style={{
          fontFamily: 'var(--font-heading)'
        }} data-id="element-94" data-mp="1014rtqhcbyfo">
            Por eso creamos el
          </h2>
          <h2 className="text-4xl md:text-5xl font-bold uppercase mb-4" style={{
          fontFamily: 'var(--font-heading)',
          color: 'var(--color-gold)'
        }} data-id="element-95" data-mp="q2ox7dj3u71f">
            KIT MENTE CLARA & ACTIVA 🧠
          </h2>
          <p className="text-green-100 text-lg" data-id="element-96" data-mp="6heynflc7g86">
            El cuadernillo digital de estimulación cognitiva diseñado para ser
            usado directamente, sin preparación previa.
          </p>
        </div>
      </section>

      {/* ── PRODUCT FEATURES ── */}
      <section className="w-full bg-white py-16 px-4" data-id="element-97" data-mp="ge1ji4dny7a7">
        <div className="max-w-3xl mx-auto" data-id="element-98" data-mp="uecbxbumftxh">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12 uppercase" style={{
          fontFamily: 'var(--font-heading)'
        }} data-id="element-99" data-mp="udaw6u2nuj9f">
            ¿Qué incluye el kit?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-100" data-mp="z5kky6s1hda4">
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-101" data-mp="1d5lm7nc7umq4">
              <div className="text-3xl mb-3" data-id="element-102" data-mp="1d3izpda9vbq5">📋</div>
              <h3 className="font-bold text-gray-900 text-lg mb-2" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-103" data-mp="1951ooinqdh3g">
                +150 actividades en 23 temáticas
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-104" data-mp="3cck6e1qbtep5">
                Organizadas por tipo de estimulación: atención, memoria,
                coordinación, percepción visual y lógica.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-105" data-mp="2jowrn3bqliaf">
              <div className="text-3xl mb-3" data-id="element-106" data-mp="2omg3jwkypiz7">✅</div>
              <h3 className="font-bold text-gray-900 text-lg mb-2" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-107" data-mp="2on7zx9mdvkqj">
                Instrucciones claras incluidas
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-108" data-mp="8wlko1iw5cyf">
                El adulto mayor puede seguirlas solo o con mínima guía. Cero
                preparación extra de tu parte.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-109" data-mp="1xey5i3a4fwfe">
              <div className="text-3xl mb-3" data-id="element-110" data-mp="1texgxptb64um">🎯</div>
              <h3 className="font-bold text-gray-900 text-lg mb-2" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-111" data-mp="1on5y61ge93f1">
                Adaptable a distintos niveles
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-112" data-mp="1eqjc59cc06bf">
                Elegís las actividades que mejor se ajustan a cada paciente, sin
                necesidad de modificar nada.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-113" data-mp="15q4pa5jpyj5t">
              <div className="text-3xl mb-3" data-id="element-114" data-mp="10r1j2hs066w9">🖨️</div>
              <h3 className="font-bold text-gray-900 text-lg mb-2" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-115" data-mp="302ktl0syfn81">
                PDF listo para imprimir o usar en pantalla
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-116" data-mp="3iyjn9yfwr81r">
                Descargás y empezás. Sin apps, sin plataformas, sin
                complicaciones.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-117" data-mp="3a5zwfk5uy7rh">
              <div className="text-3xl mb-3" data-id="element-118" data-mp="3a3vh862idmbw">♾️</div>
              <h3 className="font-bold text-gray-900 text-lg mb-2" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-119" data-mp="1rhjb4128210">
                Imprimís sin límite
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-120" data-mp="3o00pbxa4p0jx">
                Un solo pago y podés usarlo con todos tus pacientes todas las
                veces que necesites.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-121" data-mp="2hhaob16dx268">
              <div className="text-3xl mb-3" data-id="element-122" data-mp="2hf7us760wqwr">🏅</div>
              <h3 className="font-bold text-gray-900 text-lg mb-2" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-123" data-mp="29wqdvfvti9tr">
                Sin material infantil
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-124" data-mp="2dzn0dhohaq2z">
                Actividades diseñadas para adultos. Respetan la dignidad e
                inteligencia del mayor.
              </p>
            </div>
          </div>

          {/* Scientific backing */}
          <div className="mt-8 rounded-xl p-6 border-l-4 text-white" style={{
          backgroundColor: 'var(--color-green-dark)',
          borderLeftColor: 'var(--color-gold)'
        }} data-id="element-125" data-mp="1mabgi2933wbg">
            <p className="font-bold text-lg mb-1" style={{
            fontFamily: 'var(--font-heading)'
          }} data-id="element-126" data-mp="17j2mzi229qt1">
              📐 Con respaldo científico
            </p>
            <p className="text-green-100 text-sm leading-relaxed" data-id="element-127" data-mp="hzpmaoxsswb5">
              Basado en Neuroplasticidad, Reserva Cognitiva, modelo de Sohlberg
              & Mateer y Gimnasia Cerebral de Paul Dennison. Podés mencionarlo
              en cualquier plan de trabajo o informe profesional.
            </p>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className="w-full bg-gray-50 py-16 px-4" data-id="element-128" data-mp="10y1ua7cz4055">
        <div className="max-w-3xl mx-auto" data-id="element-129" data-mp="10vfn48tzvmng">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4 uppercase" style={{
          fontFamily: 'var(--font-heading)'
        }} data-id="element-130" data-mp="14yc09k0rmqk0">
            Con este kit vas a poder...
          </h2>
          <p className="text-center text-gray-500 mb-10" data-id="element-131" data-mp="1eqrjeknstl6h">
            Resultados concretos para tu trabajo diario
          </p>

          <div className="space-y-4" data-id="element-132" data-mp="1oml5ovrbkt1g">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-133" data-mp="1onmeetkv53g0">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
              backgroundColor: 'var(--color-green-dark)'
            }} data-id="element-134" data-mp="396gfw3zx7jw9">
                <CheckIcon className="w-4 h-4 text-white" data-id="element-135" data-mp="2u8ebxsoz8weh" />
              </div>
              <div data-id="element-136" data-mp="3rvn3a4bnszoe">
                <p className="font-bold text-gray-900" data-id="element-137" data-mp="3rw6aor3oo5o6">
                  Llegar a cada encuentro con todo listo
                </p>
                <p className="text-gray-600 text-sm mt-1" data-id="element-138" data-mp="3mx2488858m1j">
                  — sin improvisar, sin buscar, sin adaptaciones de urgencia.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-139" data-mp="3d5z3x8n1gb3b">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
              backgroundColor: 'var(--color-green-dark)'
            }} data-id="element-140" data-mp="eibfs4mvfj1z">
                <CheckIcon className="w-4 h-4 text-white" data-id="element-141" data-mp="1vc13dw5nqug3" />
              </div>
              <div data-id="element-142" data-mp="1ve43j90iy3yj">
                <p className="font-bold text-gray-900" data-id="element-143" data-mp="1r9wbb1uuy5vk">
                  Ofrecer actividades que respetan al adulto mayor
                </p>
                <p className="text-gray-600 text-sm mt-1" data-id="element-144" data-mp="1mcntj2dmg3uu">
                  — nada infantil, nada condescendiente.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-145" data-mp="2ezid7x2m6z79">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
              backgroundColor: 'var(--color-green-dark)'
            }} data-id="element-146" data-mp="2a80cna4i70mt">
                <CheckIcon className="w-4 h-4 text-white" data-id="element-147" data-mp="32r5wptxjr7zi" />
              </div>
              <div data-id="element-148" data-mp="2xxbekuxlb12j">
                <p className="font-bold text-gray-900" data-id="element-149" data-mp="2xwrup8odq2lv">
                  Justificar el material con base científica
                </p>
                <p className="text-gray-600 text-sm mt-1" data-id="element-150" data-mp="3a82esjeenjyu">
                  — incluilo en cualquier plan de trabajo o informe profesional.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-151" data-mp="3j9sh4k9ts3be">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
              backgroundColor: 'var(--color-green-dark)'
            }} data-id="element-152" data-mp="3o7lmjb3b0tam">
                <CheckIcon className="w-4 h-4 text-white" data-id="element-153" data-mp="1v54sh9zm5hu" />
              </div>
              <div data-id="element-154" data-mp="6rls599d4ftv">
                <p className="font-bold text-gray-900" data-id="element-155" data-mp="6tyahnj18s7g">
                  Reducir la carga de planificación
                </p>
                <p className="text-gray-600 text-sm mt-1" data-id="element-156" data-mp="fsjyvzb6r1g5">
                  — y enfocarte en el acompañamiento real.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-157" data-mp="2btlqk37cca89">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
              backgroundColor: 'var(--color-green-dark)'
            }} data-id="element-158" data-mp="2llrijcxlerng">
                <CheckIcon className="w-4 h-4 text-white" data-id="element-159" data-mp="2vimf3qab2hz3" />
              </div>
              <div data-id="element-160" data-mp="2qlnnbee46n42">
                <p className="font-bold text-gray-900" data-id="element-161" data-mp="2qk2t5pvpp66q">
                  Ver al adulto mayor comprometido y activo
                </p>
                <p className="text-gray-600 text-sm mt-1" data-id="element-162" data-mp="34i9j8juf61vu">
                  — lo que valida tu trabajo y fortalece el vínculo.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-163" data-mp="1a6uofomjxc98">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
              backgroundColor: 'var(--color-green-dark)'
            }} data-id="element-164" data-mp="159vyzjdu1thd">
                <CheckIcon className="w-4 h-4 text-white" data-id="element-165" data-mp="383co7bpk7x35" />
              </div>
              <div data-id="element-166" data-mp="3c3wm4hhyj4qc">
                <p className="font-bold text-gray-900" data-id="element-167" data-mp="3c3d5d441mmdw">
                  Trabajar con más calma y seguridad
                </p>
                <p className="text-gray-600 text-sm mt-1" data-id="element-168" data-mp="2y2sls5qb0mjc">
                  — porque llegás preparado a cada sesión.
                </p>
              </div>
            </div>
          </div>

          {/* Mid-page CTA */}
          <div className="mt-12 text-center" data-id="element-169" data-mp="2p61qhvjktbpt">
            <CTAButton scrollToOffer className="w-full max-w-md mx-auto block" data-id="element-170" data-mp="2t4iptswr9hwo">
              ¡SÍ, QUIERO TRABAJAR MEJOR AHORA!
            </CTAButton>
          </div>
        </div>
      </section>

      {/* ── BONUSES ── */}
      <section className="w-full py-16 px-4 text-white" style={{
      backgroundColor: 'var(--color-green-dark)'
    }} data-id="element-171" data-mp="rddxeun00dba">
        <div className="max-w-3xl mx-auto" data-id="element-172" data-mp="3ttub8ahrrgvs">
          <div className="text-center mb-10" data-id="element-173" data-mp="3tu3yvrfcdbyb">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-bold uppercase tracking-widest mb-4" style={{
            backgroundColor: 'var(--color-gold)',
            color: '#1a1a1a',
            fontFamily: 'var(--font-heading)'
          }} data-id="element-174" data-mp="3ou7uvs6n9kir">
              🎁 Bonus Exclusivos — Solo por hoy
            </span>
            <h2 className="text-3xl md:text-4xl font-bold uppercase mb-3" style={{
            fontFamily: 'var(--font-heading)'
          }} data-id="element-175" data-mp="363zydsxg9sbj">
              Además, con tu compra hoy recibís estos BONUS GRATIS
            </h2>
            <p className="text-green-200 text-lg" data-id="element-176" data-mp="2w7wvgkurxpnj">
              Material adicional para que nunca te falten actividades — ideal
              para encuentros extra o trabajo del adulto en casa.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-177" data-mp="1bnhztlxbds9y">
            {/* Bonus 1 */}
            <div className="bg-white/10 rounded-2xl p-6 border border-white/20 text-center" data-id="element-178" data-mp="1gmuvmw65y9oo">
              <div className="inline-block px-3 py-1 rounded-full text-sm font-bold uppercase mb-3" style={{
              backgroundColor: 'var(--color-gold)',
              color: '#1a1a1a',
              fontFamily: 'var(--font-heading)'
            }} data-id="element-179" data-mp="1llxrpr4615ki">
                GRATIS — Solo hoy
              </div>
              <div className="text-2xl mb-2" data-id="element-180" data-mp="xv24z8ekh0k4">🔢</div>
              <h3 className="font-bold text-xl mb-2" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-181" data-mp="12nd4ia5eakc5">
                300 Sudokus Progresivos para Adultos Mayores
              </h3>
              <p className="text-green-200 text-sm leading-relaxed" data-id="element-182" data-mp="a4fxpjcdzkb6">
                Distintos niveles de dificultad para entrenar concentración,
                lógica y agilidad mental de forma entretenida.
              </p>
            </div>

            {/* Bonus 2 */}
            <div className="bg-white/10 rounded-2xl p-6 border border-white/20 text-center" data-id="element-183" data-mp="bve83jjx8pkz">
              <div className="inline-block px-3 py-1 rounded-full text-sm font-bold uppercase mb-3" style={{
              backgroundColor: 'var(--color-gold)',
              color: '#1a1a1a',
              fontFamily: 'var(--font-heading)'
            }} data-id="element-184" data-mp="6xcu3d1crggd">
                GRATIS — Solo hoy
              </div>
              <div className="text-2xl mb-2" data-id="element-185" data-mp="3kbg29si79aea">🔤</div>
              <h3 className="font-bold text-xl mb-2" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-186" data-mp="3ke2cy10e060q">
                +50 Sopas de Letras ActivaMente
              </h3>
              <p className="text-green-200 text-sm leading-relaxed" data-id="element-187" data-mp="2hi28f87jkinj">
                Actividades de búsqueda de palabras que fortalecen memoria,
                atención y velocidad mental.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING / OFFER BOX ── */}
      <section id="compra" className="w-full py-16 px-4" style={{
      backgroundColor: '#0a0a0a'
    }} data-id="element-188" data-mp="2diba8jfor08z">
        <div className="max-w-2xl mx-auto" data-id="element-189" data-mp="358oggzhjq2xj">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4 uppercase" style={{
          fontFamily: 'var(--font-heading)'
        }} data-id="element-190" data-mp="357mw24en4435">
            🎁 Elegí tu pack hoy
          </h2>
          <p className="text-center text-gray-400 mb-8" data-id="element-191" data-mp="3k5yqe4ejj0r5">
            Seleccioná la opción que mejor se adapte a tus necesidades
          </p>

          <div className="border-2 rounded-2xl overflow-hidden shadow-2xl" style={{
          borderColor: 'var(--color-orange)',
          backgroundColor: '#111'
        }} data-id="element-192" data-mp="3oxqgyexp56ha">
            {/* Offer header */}
            <div className="py-4 px-6 text-center text-white" style={{
            backgroundColor: 'var(--color-orange)'
          }} data-id="element-193" data-mp="1f4ma6szw2lyn">
              <p className="font-bold text-lg uppercase tracking-wide" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-194" data-mp="1a3or8e721nw3">
                🔴 Oferta por tiempo limitado — vence cuando termina el contador
              </p>
            </div>

            <div className="p-6 md:p-8" data-id="element-195" data-mp="1ttshz5cslki5">
              {/* Countdown */}
              <div className="flex justify-center mb-8" data-id="element-196" data-mp="1tvlcxne1mgab">
                <div className="rounded-xl px-6 py-4 w-full max-w-xs mx-auto border border-gray-800 flex flex-col items-center" style={{
                backgroundColor: '#1a1a1a'
              }} data-id="element-197" data-mp="mh04nf73p795">
                  <p className="text-gray-300 text-sm mb-3 uppercase tracking-wide font-semibold text-center" style={{
                  fontFamily: 'var(--font-heading)'
                }} data-id="element-198" data-mp="2y3agjq0ltcit">
                    Esta oferta vence en:
                  </p>
                  <CountdownTimer data-id="element-199" data-mp="3cszu6kpvv8zz" />
                </div>
              </div>

              {/* Pricing Options */}
              <div className="space-y-4 mb-8" data-id="element-200" data-mp="3hq8gmxi2dz71">
                {/* Base Plan */}
                <div onClick={() => setSelectedPlan('base')} className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 flex items-start gap-4 ${selectedPlan === 'base' ? 'border-emerald-400 bg-[#1a1a1a] shadow-[0_0_12px_rgba(52,211,153,0.25)]' : 'border-gray-800 bg-[#111] hover:border-gray-600'}`} data-id="element-201" data-mp="3lsd2vb66ntjn">
                  {/* Radio button */}
                  <div className="mt-1 flex-shrink-0" data-id="element-202" data-mp="1hyyn1dk5y1vq">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedPlan === 'base' ? 'border-emerald-400' : 'border-gray-600'}`} data-id="element-203" data-mp="1mq6wg9lhtn8w">
                      {selectedPlan === 'base' && <div className="w-3 h-3 rounded-full bg-emerald-400" data-id="element-204" data-mp="1cykpwqk3occh" />}
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col md:flex-row md:justify-between md:items-start gap-4" data-id="element-205" data-mp="25pkwn99tfu7u">
                    <div data-id="element-206" data-mp="1roi88pvcm7tr">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2" style={{
                      fontFamily: 'var(--font-heading)'
                    }} data-id="element-207" data-mp="1rpa3fzl1a3ji">
                        <span data-id="element-208" data-mp="kc8b1gvu53jl">💚</span> PACK BASE
                      </h3>
                      <div className="mt-2 text-gray-400 text-sm space-y-1" data-id="element-209" data-mp="cs762ctqf749">
                        <p data-id="element-210" data-mp="cua5tokosem6">• 1 tomo principal</p>
                        <p data-id="element-211" data-mp="7sces861ofio">• 2 bonus de regalo</p>
                      </div>
                    </div>

                    <div className="text-left md:text-right" data-id="element-212" data-mp="mplnofre61y5">
                      <p className="text-gray-500 line-through text-sm" data-id="element-213" data-mp="hrk0pjquhihe">
                        $37.900
                      </p>
                      <p className="text-3xl font-bold text-white" style={{
                      fontFamily: 'var(--font-heading)'
                    }} data-id="element-214" data-mp="3q8ewkf9862lz">
                        $18.790
                      </p>
                    </div>
                  </div>
                </div>

                {/* Premium Plan */}
                <div onClick={() => setSelectedPlan('premium')} className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 flex items-start gap-4 ${selectedPlan === 'premium' ? 'border-emerald-400 bg-[#1a1a1a] shadow-[0_0_18px_rgba(52,211,153,0.3)]' : 'border-gray-800 bg-[#111] hover:border-gray-600'}`} data-id="element-215" data-mp="37fu777sqifmx">
                  {/* Radio button */}
                  <div className="mt-1 flex-shrink-0" data-id="element-216" data-mp="4tmadfvd9t6y">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedPlan === 'premium' ? 'border-emerald-400' : 'border-gray-600'}`} data-id="element-217" data-mp="9sgucl52lnsn">
                      {selectedPlan === 'premium' && <div className="w-3 h-3 rounded-full bg-emerald-400" data-id="element-218" data-mp="3i7qptaegfurg" />}
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col md:flex-row md:justify-between md:items-start gap-4" data-id="element-219" data-mp="3n7mwxiig82b8">
                    <div data-id="element-220" data-mp="xh0311s182l9">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2 mb-2" style={{
                      fontFamily: 'var(--font-heading)'
                    }} data-id="element-221" data-mp="xjvv49h77x1s">
                        <span data-id="element-222" data-mp="2i02d3rw6j26w">🏆</span> PACK PREMIUM
                      </h3>

                      <div className="flex flex-wrap gap-2 mb-3" data-id="element-223" data-mp="2d7si3xg7jbxr">
                        <span className="bg-[var(--color-green-dark)] text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1" data-id="element-224" data-mp="28a0p103ttjrz">
                          ⭐ MÁS ELEGIDO
                        </span>
                        <span className="border border-[var(--color-green-dark)] text-[var(--color-green-dark)] text-xs font-bold px-2 py-1 rounded-full" data-id="element-225" data-mp="1z8t3limesdfb">
                          AHORRÁS $27.910
                        </span>
                      </div>

                      <div className="text-gray-300 text-sm space-y-1" data-id="element-226" data-mp="1pczg5475izir">
                        <p className="text-[var(--color-gold)] font-semibold" data-id="element-227" data-mp="3onh4amln06xi">
                          • 2 tomos principales
                        </p>
                        <p data-id="element-228" data-mp="3rpvfennhisc8">• 2 bonus de regalo</p>
                        <p className="text-[var(--color-gold)] font-semibold" data-id="element-229" data-mp="3rqdhw20qq2sl">
                          • +150 Mandalas Terapéuticas
                        </p>
                      </div>
                    </div>

                    <div className="text-left md:text-right" data-id="element-230" data-mp="3htijrk8dq4eu">
                      <p className="text-gray-500 line-through text-sm" data-id="element-231" data-mp="3mp7nlzrfm7gy">
                        $55.900
                      </p>
                      <p className="text-3xl font-bold text-white" style={{
                      fontFamily: 'var(--font-heading)'
                    }} data-id="element-232" data-mp="38x16obggfwgg">
                        $27.990
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-center text-gray-400 text-sm mb-4" data-id="element-233" data-mp="2q0rwhz8obq1q">
                Hasta 3 cuotas fijas sin interés
              </p>

              {/* Main CTA */}
              <CTAButton className="w-full block text-xl py-6" href={selectedPlan === 'premium' ? CHECKOUT_URL_PREMIUM : CHECKOUT_URL} data-id="element-234" data-mp="2bcrltcind2u7">
                ✅ SÍ, QUIERO EL{' '}
                {selectedPlan === 'premium' ? 'PACK PREMIUM' : 'PACK BASE'}
              </CTAButton>

              {/* Trust row */}
              <div className="flex items-center justify-center gap-4 mt-5 text-gray-400 text-sm flex-wrap" data-id="element-235" data-mp="2uaufi4m53nyc">
                <span className="flex items-center gap-1.5" data-id="element-236" data-mp="2pd2duivp0ngy">
                  <MailIcon className="w-4 h-4 text-[var(--color-green-dark)]" data-id="element-237" data-mp="333nx48p26zfd" />
                  Acceso inmediato
                </span>
                <span className="flex items-center gap-1.5" data-id="element-238" data-mp="2z3x7w9f6krcy">
                  <ShieldCheckIcon className="w-4 h-4 text-[var(--color-green-dark)]" data-id="element-239" data-mp="3d3ycnzykgixp" />
                  Un solo pago
                </span>
                <span className="flex items-center gap-1.5" data-id="element-240" data-mp="381pgyvub72rw">
                  <PrinterIcon className="w-4 h-4 text-[var(--color-green-dark)]" data-id="element-241" data-mp="25rfedmmi8me1" />
                  Descarga digital
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="w-full bg-gray-50 py-16 px-4" data-id="element-242" data-mp="2jhsljmrknx2b">
        <div className="max-w-3xl mx-auto" data-id="element-243" data-mp="2jjdgvelpnk18">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-3 uppercase" style={{
          fontFamily: 'var(--font-heading)'
        }} data-id="element-244" data-mp="2ogvsjcr44dcx">
            Lo que dicen quienes ya lo usan ❤️
          </h2>
          <p className="text-center text-gray-500 mb-10" data-id="element-245" data-mp="2xid36flr3mu8">
            Acompañantes, cuidadores y profesionales de la salud
          </p>

          <div className="space-y-5" data-id="element-246" data-mp="133spwnzretkv">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-247" data-mp="135x35wjjt4xt">
              <StarRating data-id="element-248" data-mp="182vt02w6ipq7" />
              <p className="text-gray-700 mt-3 leading-relaxed italic" data-id="element-249" data-mp="10j4jv31sxuoh">
                "Trabajo con adultos mayores y este kit me re sirvió. Les gustan
                las actividades porque no son difíciles ni para chicos. Los veo
                más animados cuando hacemos los ejercicios."
              </p>
              <div className="mt-4 flex items-center gap-3" data-id="element-250" data-mp="luqh0s4dy67x">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{
                backgroundColor: 'var(--color-green-dark)',
                fontFamily: 'var(--font-heading)'
              }} data-id="element-251" data-mp="ls5ctwgm7e70">
                  PM
                </div>
                <div data-id="element-252" data-mp="15h5zfe00rbv7">
                  <p className="font-bold text-gray-900 text-sm" data-id="element-253" data-mp="15ho32vp0gsd5">
                    Paula Medina
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-254" data-mp="3u7xeob6dlfrg">
                    Acompañante · Buenos Aires
                  </p>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-255" data-mp="1y6vpcdxp0j2i">
              <StarRating data-id="element-256" data-mp="1y4iy1a61owcs" />
              <p className="text-gray-700 mt-3 leading-relaxed italic" data-id="element-257" data-mp="1k7nlh7ql4mrz">
                "Lo que más valoro es llegar al encuentro con todo preparado.
                Antes perdía tiempo buscando qué hacer. Ahora abro el kit y
                listo. Me cambió el día a día de trabajo."
              </p>
              <div className="mt-4 flex items-center gap-3" data-id="element-258" data-mp="2gxwjmonvbqfq">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{
                backgroundColor: 'var(--color-green-dark)',
                fontFamily: 'var(--font-heading)'
              }} data-id="element-259" data-mp="2gz7o6qp5127w">
                  LR
                </div>
                <div data-id="element-260" data-mp="30mfd3ifwpq08">
                  <p className="font-bold text-gray-900 text-sm" data-id="element-261" data-mp="30movajupmsvd">Luciana R.</p>
                  <p className="text-gray-500 text-xs" data-id="element-262" data-mp="39yv06oy6chrx">
                    Acompañante Terapéutica · Córdoba
                  </p>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-263" data-mp="3jp7fa1n7ziqo">
              <StarRating data-id="element-264" data-mp="3erfppea1s8pn" />
              <p className="text-gray-700 mt-3 leading-relaxed italic" data-id="element-265" data-mp="3et8ok7ef5on3">
                "Tengo pacientes con distintos niveles y el kit me permite
                adaptar fácil. Las instrucciones son claras y los adultos las
                entienden solos. Muy buen material."
              </p>
              <div className="mt-4 flex items-center gap-3" data-id="element-266" data-mp="30x4x2luxjueb">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{
                backgroundColor: 'var(--color-green-dark)',
                fontFamily: 'var(--font-heading)'
              }} data-id="element-267" data-mp="2vzmz1qigazl3">
                  MG
                </div>
                <div data-id="element-268" data-mp="1yl2wxzg8mhfm">
                  <p className="font-bold text-gray-900 text-sm" data-id="element-269" data-mp="1yll0lh58bxxk">Marcela G.</p>
                  <p className="text-gray-500 text-xs" data-id="element-270" data-mp="2d9fuv1yvyacr">
                    Cuidadora domiciliaria · Rosario
                  </p>
                </div>
              </div>
            </div>

            {/* Testimonial 4 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-271" data-mp="1kk8ppk4xemij">
              <StarRating data-id="element-272" data-mp="1fmgyksc3v4fe" />
              <p className="text-gray-700 mt-3 leading-relaxed italic" data-id="element-273" data-mp="1fn8phpurcdzc">
                "Trabajo en un geriátrico y lo uso en grupos pequeños. Las
                actividades son perfectas para dinamizar los encuentros. Los
                abuelos se ríen, compiten y se nota el cambio en su estado de
                ánimo."
              </p>
              <div className="mt-4 flex items-center gap-3" data-id="element-274" data-mp="30za8hlr142y2">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{
                backgroundColor: 'var(--color-green-dark)',
                fontFamily: 'var(--font-heading)'
              }} data-id="element-275" data-mp="30yqnfw630yft">
                  VT
                </div>
                <div data-id="element-276" data-mp="33j6ijurh923n">
                  <p className="font-bold text-gray-900 text-sm" data-id="element-277" data-mp="33krdhlhpen21">Valeria T.</p>
                  <p className="text-gray-500 text-xs" data-id="element-278" data-mp="3r8r5spunyliq">
                    Trabajadora de geriátrico · Mendoza
                  </p>
                </div>
              </div>
            </div>

            {/* Testimonial 5 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-279" data-mp="3hippwlcgs9ew">
              <StarRating data-id="element-280" data-mp="3mhiufo9rwq2x" />
              <p className="text-gray-700 mt-3 leading-relaxed italic" data-id="element-281" data-mp="3mgr9r8hpscrf">
                "Soy AT hace 6 años y nunca había encontrado un material tan
                completo y tan fácil de usar. El respaldo científico me permite
                incluirlo en mis informes sin problema. Lo recomiendo a todas
                mis colegas."
              </p>
              <div className="mt-4 flex items-center gap-3" data-id="element-282" data-mp="npv204sq7ye5">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{
                backgroundColor: 'var(--color-green-dark)',
                fontFamily: 'var(--font-heading)'
              }} data-id="element-283" data-mp="nojvvybt6ejv">
                  NF
                </div>
                <div data-id="element-284" data-mp="4yu0pa44vqrf">
                  <p className="font-bold text-gray-900 text-sm" data-id="element-285" data-mp="4ykapmj39jlq">Natalia F.</p>
                  <p className="text-gray-500 text-xs" data-id="element-286" data-mp="1lixamwdoqp">
                    Acompañante Terapéutica · Tucumán
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── GUARANTEE ── */}
      <section className="w-full bg-white py-16 px-4" data-id="element-287" data-mp="3i6osp8nt6oxg">
        <div className="max-w-2xl mx-auto" data-id="element-288" data-mp="3i76vyp8w23ev">
          <div className="border-2 border-green-200 rounded-2xl p-8 text-center bg-green-50" data-id="element-289" data-mp="1xomjp071xq39">
            <div className="text-6xl mb-4" data-id="element-290" data-mp="22n7dagrstbiq">🛡️</div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 uppercase" style={{
            fontFamily: 'var(--font-heading)'
          }} data-id="element-291" data-mp="22lw8qeqj3zqk">
              Garantía de satisfacción — 7 días
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg" data-id="element-292" data-mp="1eyxuzotsn5wc">
              Si en los próximos 7 días sentís que el kit no te sirve en tu
              trabajo, nos escribís y te devolvemos el{' '}
              <strong data-id="element-293" data-mp="1nxi6o60xsfb2">100% de tu dinero</strong>. Sin preguntas, sin vueltas.
              Porque sabemos que cuando lo uses, va a quedar en tu caja de
              herramientas para siempre.
            </p>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="w-full bg-gray-50 py-16 px-4" data-id="element-294" data-mp="108guc7ozgk0i">
        <div className="max-w-2xl mx-auto" data-id="element-295" data-mp="106vzsi2o512n">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4 uppercase" style={{
          fontFamily: 'var(--font-heading)'
        }} data-id="element-296" data-mp="h3u78a6eez5z">
            Preguntas frecuentes
          </h2>
          <p className="text-center text-gray-500 mb-10" data-id="element-297" data-mp="qw9nn321rfor">
            Todo lo que necesitás saber antes de comprar
          </p>
          <FAQAccordion data-id="element-298" data-mp="zulaqv3pfvcg" />
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="w-full py-16 px-4 text-white" style={{
      backgroundColor: 'var(--color-green-dark)'
    }} data-id="element-299" data-mp="zwo8k1b3msrq">
        <div className="max-w-2xl mx-auto text-center" data-id="element-300" data-mp="19rq1z9ceqh04">
          <h2 className="text-3xl md:text-4xl font-bold uppercase mb-4" style={{
          fontFamily: 'var(--font-heading)'
        }} data-id="element-301" data-mp="14u7pj8z6ljnd">
            Llegá a tu próximo encuentro con todo listo.
          </h2>
          <p className="text-green-200 text-lg mb-8 leading-relaxed" data-id="element-302" data-mp="36mvw9sb1ap7w">
            Sin improvisar. Sin buscar. Sin adaptaciones de último momento. El
            Kit Mente Clara & Activa es la herramienta que te acompaña en cada
            sesión.
          </p>

          <CTAButton className="w-full max-w-md mx-auto block text-xl py-6" data-id="element-303" data-mp="1ce8vaf93zk3r">
            ✅ ACCEDER AL KIT AHORA
          </CTAButton>

          <div className="flex items-center justify-center gap-6 mt-6 text-green-200 text-sm flex-wrap" data-id="element-304" data-mp="1hazaz1h04don">
            <span className="flex items-center gap-1.5" data-id="element-305" data-mp="13bhf625ag05s">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-306" data-mp="17emha3lghztw" />
              Compra segura
            </span>
            <span className="flex items-center gap-1.5" data-id="element-307" data-mp="tfegj69najnl">
              <MailIcon className="w-4 h-4" data-id="element-308" data-mp="tgxwevre2sou" />
              Acceso inmediato
            </span>
            <span className="flex items-center gap-1.5" data-id="element-309" data-mp="yfsf7xpcwh8y">
              <span data-id="element-310" data-mp="2ntnrvhxeo9yc">🛡️</span>
              Garantía 7 días
            </span>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="w-full bg-gray-900 py-6 px-4 text-center text-gray-500 text-sm" data-id="element-311" data-mp="2spctdqszk0xa">
        <p data-id="element-312" data-mp="2r8gyvjvibjcx">© 2025 Kit Mente Clara & Activa. Todos los derechos reservados.</p>
        <p className="mt-1" data-id="element-313" data-mp="2m9vx3g0lwrmd">
          Producto digital — Acceso inmediato vía correo electrónico
        </p>
      </footer>
    </div>;
}